import {Routes} from '@angular/router';
import {IndexComponent} from './pages/index/index.component';
import {HomeComponent} from './pages/home/home.component';
import {LoginComponent} from './pages/login/login.component';
import {ForbiddenComponent} from './pages/forbidden/forbidden.component';
import {authGuard} from './core/guards/auth.guard';
import {
  ROLE_ARTICLES_MANAGE,
  ROLE_ARTICLES_WRITE,
  ROLE_PIGLET_LOGIN,
  ROLE_SUPER_ADMINISTRATOR
} from './core/models/user/user-info.model';
import {DefaultLayoutComponent} from './layout/default-layout/default-layout.component';
import {NotFoundComponent} from './pages/not-found/not-found.component';
import {RegisterComponent} from './pages/register/register.component';
import {ForgotPasswordComponent} from './pages/forgot-password/forgot-password.component';
import {ResetPasswordComponent} from './pages/reset-password/reset-password.component';
import {ArticlesComponent} from './pages/article/articles/articles.component';
import {ArticleDetailComponent} from './pages/article/article-detail/article-detail.component';
import {DownloadClientComponent} from './pages/download/client/download-client.component';
import {DownloadPatchesComponent} from './pages/download/patches/download-patches.component';
import {GuidesComponent} from './pages/guides/guides.component';
import {ConquerAccountsComponent} from './pages/user/conquer-accounts/conquer-accounts.component';
import {SettingsProfileComponent} from './pages/user/settings/settings-profile/settings-profile.component';
import {ConquerVipComponent} from './pages/user/conquer-vip/conquer-vip.component';
import {SettingsIndexComponent} from './pages/user/settings/settings-index/settings-index.component';
import {UserLayoutComponent} from './layout/user-layout/user-layout.component';
import {noAuthGuard} from './core/guards/no-auth.guard';
import {AdminLayoutComponent} from './layout/admin-layout/admin-layout.component';
import {DashboardComponent} from './pages/admin/dashboard/dashboard.component';
import {PigletArticleListComponent} from './pages/admin/articles/piglet-article-list/piglet-article-list.component';
import {PigletEditArticleComponent} from './pages/admin/articles/piglet-edit-article/piglet-edit-article.component';
import {ApplicationSettingsComponent} from './pages/admin/settings/application-settings/application-settings.component';

export const routes: Routes = [
  {
    path: '',
    component: DefaultLayoutComponent,
    children: [
      {
        path: '',
        component: IndexComponent,
        data: {
          title: 'INDEX.TITLE',
        }
      },
      {
        path: 'home',
        component: HomeComponent,
        data: {
          title: 'HOME.TITLE',
        }
      },
      {
        path: 'download',
        children: [
          {
            path: '',
            component: DownloadClientComponent,
            data: {
              title: 'DOWNLOADS.CLIENT_TITLE',
            }
          },
          {
            path: 'client',
            component: DownloadClientComponent,
            data: {
              title: 'DOWNLOADS.CLIENT_TITLE',
            }
          },
          {
            path: 'patches',
            component: DownloadPatchesComponent,
            data: {
              title: 'DOWNLOADS.PATCHES_TITLE',
            }
          },
        ]
      },
      {
        path: 'news',
        component: ArticlesComponent,
        data: {
          articleType: 1,
          itemsPerPage: 15,
          pageName: 'News',
          title: 'NEWS.TITLE'
        }
      },
      {
        path: 'guides',
        component: GuidesComponent
      },
      {
        path: 'terms',
        component: ArticleDetailComponent,
        data: {
          articleId: 91,
          pageName: 'TOS',
          title: 'TERMS_OF_SERVICE.TITLE'
        }
      },
      {
        path: 'privacy',
        component: ArticleDetailComponent,
        data: {
          articleId: 92,
          pageName: 'PRIVACY_POLICY',
          title: 'PRIVACY_POLICY.TITLE'
        }
      },
      {
        path: 'refunds-policy',
        component: ArticleDetailComponent,
        data: {
          articleId: 93,
          pageName: 'REFUNDS_POLICY',
          title: 'REFUNDS_POLICY.TITLE'
        }
      },
      {
        path: 'read/:articleId',
        component: ArticleDetailComponent,
        data: {
          pageName: 'ARTICLE'
        }
      },
    ]
  },
  {
    path: 'user',
    component: DefaultLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'conquer-accounts',
        pathMatch: 'full',
      },
      {
        path: 'conquer-accounts',
        component: ConquerAccountsComponent,
        data: {
          title: 'USER_CP.CONQUER_ACCOUNTS',
        }
      },
      {
        path: 'conquer-vip',
        component: ConquerVipComponent,
        data: {
          title: 'USER_CP.CONQUER_VIP',
        }
      }
    ]
  },
  {
    path: 'settings',
    component: UserLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'general',
        pathMatch: 'full',
      },
      {
        path: 'general',
        component: SettingsIndexComponent,
        data: {
          title: 'USER_CP.SETTINGS',
        }
      },
      {
        path: 'profile',
        component: SettingsProfileComponent,
        data: {
          title: 'USER_CP.PROFILE',
        }
      }
    ]
  },
  {
    path: 'piglet',
    component: AdminLayoutComponent,
    canActivate: [authGuard],
    data: {
      authGuardConfig: {
        requiredRoles: [ROLE_PIGLET_LOGIN]
      }
    },
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        component: DashboardComponent,
      },
      {
        path: 'articles/list',
        component: PigletArticleListComponent,
        data: {
          authGuardConfig: {
            requiredRoles: [ROLE_ARTICLES_MANAGE]
          }
        },
      },
      {
        path: 'articles/edit/:id',
        component: PigletEditArticleComponent,
        data: {
          authGuardConfig: {
            requiredRoles: [ROLE_ARTICLES_WRITE]
          }
        },
      },
      {
        path: 'articles/create',
        component: PigletEditArticleComponent,
        data: {
          authGuardConfig: {
            requiredRoles: [ROLE_ARTICLES_WRITE]
          }
        },
      },
      {
        path: 'settings/application-settings',
        component: ApplicationSettingsComponent,
        data: {
          authGuardConfig: {
            requiredRoles: [ROLE_SUPER_ADMINISTRATOR]
          }
        }
      }
    ]
  },
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [noAuthGuard],
    data: {
      title: 'SIGNIN.TITLE',
    }
  },
  {
    path: 'signin',
    component: LoginComponent,
    canActivate: [noAuthGuard],
    data: {
      title: 'SIGNIN.TITLE',
    }
  },
  {
    path: 'register',
    component: RegisterComponent,
    canActivate: [noAuthGuard],
    data: {
      title: 'SIGNUP.TITLE',
    }
  },
  {
    path: 'signup',
    component: RegisterComponent,
    canActivate: [noAuthGuard],
    data: {
      title: 'SIGNUP.TITLE',
    }
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
    canActivate: [noAuthGuard],
    data: {
      title: 'PASSWORD.TITLE',
    }
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
    canActivate: [noAuthGuard],
    data: {
      title: 'PASSWORD.RESET_TITLE',
    }
  },
  {
    path: 'forbidden',
    component: ForbiddenComponent,
    data: {
      title: 'FORBIDDEN.TITLE',
    }
  },
  {
    path: 'not-found',
    component: NotFoundComponent,
    data: {
      title: 'NOT_FOUND.TITLE',
    }
  },
];
